import React from "react";
import { TrashIcon } from "@heroicons/react/20/solid";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useTranslation } from "react-i18next";
import ValidationErrors from "../../../../components/ValidationErrors";

const PreAuthItemsTable = ({ items = [], onEdit, onDelete }) => {
  const { t } = useTranslation();

  const toNumber = (value) => {
    const nextValue = parseFloat(value);
    return Number.isFinite(nextValue) ? nextValue : 0;
  };

  const formatAmount = (value) => toNumber(value).toFixed(2);

  const getValue = (row, ...keys) => {
    const key = keys.find(
      (field) => row?.[field] !== undefined && row?.[field] !== null,
    );
    return key ? row[key] : undefined;
  };

  const hasValidation = (row) =>
    row?.has_validation_error === true ||
    (Array.isArray(row?.validation_errors) && row.validation_errors.length > 0);

  const totals = items.reduce(
    (acc, row) => ({
      unitPrice:
        acc.unitPrice + toNumber(getValue(row, "unit_price", "unitPrice")),
      quantity: acc.quantity + toNumber(row.quantity),
      discountAmount:
        acc.discountAmount +
        toNumber(getValue(row, "discount_amount", "discountAmount")),
      taxAmount:
        acc.taxAmount + toNumber(getValue(row, "tax_amount", "taxAmount")),
      netAmount: acc.netAmount + toNumber(row.net),
      patientShare:
        acc.patientShare +
        toNumber(getValue(row, "patientShareAmount", "patient_share_amount")),
      payerShare:
        acc.payerShare + toNumber(getValue(row, "payer_share", "payerShare")),
    }),
    {
      unitPrice: 0,
      quantity: 0,
      discountAmount: 0,
      taxAmount: 0,
      netAmount: 0,
      patientShare: 0,
      payerShare: 0,
    },
  );

  const getTypeClassName = (type) => {
    const normalizedType = String(type || "").toLowerCase();

    if (normalizedType.includes("laboratory")) {
      return "bg-[#e2f8e7] text-[#239454]";
    }
    if (normalizedType.includes("procedure")) {
      return "bg-[#efe7ff] text-[#8057d9]";
    }
    if (normalizedType.includes("imaging")) {
      return "bg-[#ffefe7] text-[#cf6846]";
    }
    if (normalizedType.includes("service")) {
      return "bg-[#dcebff] text-[#3678c8]";
    }

    return "bg-[#edf2f7] text-[#607086]";
  };

  return (
    <div className="w-full overflow-x-auto rounded border border-[#e9edf3] bg-white">
      <table className="w-max min-w-full table-auto border-collapse">
        <thead>
          <tr>
            <th className="h-[38px] whitespace-nowrap border-r border-b border-[#e9edf3] bg-gray-100 px-2 py-1 !text-left align-middle !text-[14px] !font-semibold !text-gray-800 !text-center last:border-r-0">
              {t("Actions")}
            </th>
            <th className="h-[38px] whitespace-nowrap border-r border-b border-[#e9edf3] bg-gray-100 px-2 py-1 !text-left align-middle !text-[14px] !font-semibold !text-gray-800 last:border-r-0">
              {t("#")}
            </th>
             <th className="h-[38px] whitespace-nowrap border-r border-b border-[#e9edf3] bg-gray-100 px-2 py-1 !text-left align-middle !text-[14px] !font-semibold !text-gray-800 last:border-r-0">
              {t("Type")}
            </th>
            <th className="h-[38px] w-fit whitespace-nowrap border-r border-b border-[#e9edf3] bg-gray-100 px-2 py-1 !text-left align-middle !text-[14px] !font-semibold !text-gray-800 last:border-r-0">
              {t("Standard Code - Description")}
            </th>
            <th className="h-[38px] whitespace-nowrap border-r border-b border-[#e9edf3] bg-gray-100 px-2 py-1 !text-left align-middle !text-[14px] !font-semibold !text-gray-800 !text-center last:border-r-0">
              {t("Unit Price")}
            </th>
            <th className="h-[38px] whitespace-nowrap border-r border-b border-[#e9edf3] bg-gray-100 px-2 py-1 !text-left align-middle !text-[14px] !font-semibold !text-gray-800 !text-center last:border-r-0">
              {t("Qty")}
            </th>
            <th className="h-[38px] whitespace-nowrap border-r border-b border-[#e9edf3] bg-gray-100 px-2 py-1 !text-left align-middle !text-[14px] !font-semibold !text-gray-800 !text-center last:border-r-0">
              {t("Discount Amount")}
            </th>
            <th className="h-[38px] whitespace-nowrap border-r border-b border-[#e9edf3] bg-gray-100 px-2 py-1 !text-left align-middle !text-[14px] !font-semibold !text-gray-800 !text-center last:border-r-0">
              {t("Tax Amount")}
            </th>
            <th className="h-[38px] whitespace-nowrap border-r border-b border-[#e9edf3] bg-gray-100 px-2 py-1 !text-left align-middle !text-[14px] !font-semibold !text-gray-800 !text-center last:border-r-0">
              {t("Net Amount")}
            </th>
            <th className="h-[38px] whitespace-nowrap border-r border-b border-[#e9edf3] bg-gray-100 px-2 py-1 !text-left align-middle !text-[14px] !font-semibold !text-gray-800 !text-center last:border-r-0">
              {t("Patient Share")}
            </th>
            <th className="h-[38px] whitespace-nowrap border-r border-b border-[#e9edf3] bg-gray-100 px-2 py-1 !text-left align-middle !text-[14px] !font-semibold !text-gray-800 !text-center last:border-r-0">
              {t("Payer Share")}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.length > 0 ? (
            items.map((row, index) => (
              <React.Fragment key={index}>
                <tr>
                  <td className="h-8 whitespace-nowrap border-r border-b border-[#e9edf3] px-2 py-1 text-left align-middle !text-[12px] !font-normal !text-gray-800 last:border-r-0">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        type="button"
                        className="inline-flex h-[22px] w-[22px] cursor-pointer items-center justify-center rounded bg-transparent p-0 text-[#1788c9] hover:bg-[#f2f7fb]"
                        title={t("Edit")}
                        onClick={() => onEdit(index)}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 18 }} />
                      </button>
                      <button
                        type="button"
                        className="inline-flex h-[22px] w-[22px] cursor-pointer items-center justify-center rounded bg-transparent p-0 text-[#ef4f68] hover:bg-[#f2f7fb]"
                        title={t("Delete")}
                        onClick={() => onDelete(index)}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                    <td className="h-8 whitespace-nowrap border-r border-b border-[#e9edf3] px-2 py-1 text-left align-middle !text-[12px] !font-normal !text-gray-800 last:border-r-0">
                    {index + 1}
                  </td>
                  <td className="h-8 whitespace-nowrap border-r border-b border-[#e9edf3] px-2 py-1 text-left align-middle !text-[12px] !font-normal !text-gray-800 last:border-r-0">
                    <span className={`inline-flex h-[22px] items-center justify-center rounded-full px-2 text-[11px] font-semibold ${getTypeClassName(row.type)}`}>
                      {row.type || "-"}
                    </span>
                  </td>
                  <td className="h-8 w-fit whitespace-nowrap border-r border-b border-[#e9edf3] px-2 py-1 !text-left align-middle !text-[12px] !font-normal !text-gray-800 last:border-r-0">
                    {getValue(
                      row,
                      "standardCodeDescription",
                      "standardDescription",
                    ) || "-"}{" "}
                    - {getValue(row, "standardCode", "code") || "-"}
                  </td>
                  <td className="h-8 whitespace-nowrap border-r border-b border-[#e9edf3] px-2 py-1 text-center align-middle !text-[12px] !font-normal !text-gray-800 last:border-r-0">
                    {formatAmount(getValue(row, "unit_price", "unitPrice"))}
                  </td>
                  <td className="h-8 whitespace-nowrap border-r border-b border-[#e9edf3] px-2 py-1 text-center align-middle !text-[12px] !font-normal !text-gray-800 last:border-r-0">
                    {row.quantity || 0}
                  </td>
                  <td className="h-8 whitespace-nowrap border-r border-b border-[#e9edf3] px-2 py-1 text-center align-middle !text-[12px] !font-normal !text-gray-800 last:border-r-0">
                    {formatAmount(
                      getValue(row, "discount_amount", "discountAmount"),
                    )}
                  </td>
                  <td className="h-8 whitespace-nowrap border-r border-b border-[#e9edf3] px-2 py-1 text-center align-middle !text-[12px] !font-normal !text-gray-800 last:border-r-0">
                    {formatAmount(getValue(row, "tax_amount", "taxAmount"))}
                  </td>
                  <td className="h-8 whitespace-nowrap border-r border-b border-[#e9edf3] px-2 py-1 text-center align-middle !text-[12px] !font-normal !text-gray-800 last:border-r-0">
                    {formatAmount(row.net)}
                  </td>
                  <td className="h-8 whitespace-nowrap border-r border-b border-[#e9edf3] px-2 py-1 text-center align-middle !text-[12px] !font-normal !text-gray-800 last:border-r-0">
                    {formatAmount(
                      getValue(row, "patientShareAmount", "patient_share_amount"),
                    )}
                  </td>
                  <td className="h-8 whitespace-nowrap border-r border-b border-[#e9edf3] px-2 py-1 text-center align-middle !text-[12px] !font-normal !text-gray-800 last:border-r-0">
                    {formatAmount(getValue(row, "payer_share", "payerShare"))}
                  </td>
                </tr>
                {hasValidation(row) ? (
                  <tr>
                    <td
                      colSpan={11}
                      className="h-auto border-r border-b border-[#e9edf3] bg-yellow-50 px-2 py-1 text-left !text-[12px] !font-normal !text-gray-800 last:border-r-0"
                    >
                      <ValidationErrors
                        validationErrors={row.validation_errors}
                        compact
                      />
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            ))
          ) : (
            <tr>
              <td
                colSpan={11}
                className="h-8 whitespace-nowrap border-r border-b border-[#e9edf3] px-2 py-1 text-center align-middle !text-[12px] !font-normal !text-gray-800 last:border-r-0"
              >
                {t("No Data Found")}
              </td>
            </tr>
          )}
        </tbody>
        {items.length > 0 ? (
          <tfoot>
            <tr>
              <td
                colSpan={4}
                className="h-9 whitespace-nowrap border-r border-b border-[#e9edf3] bg-gray-100 px-2 py-1 text-right align-middle !text-[14px] !font-semibold !text-gray-800 last:border-r-0"
              >
                {t("Total")}
              </td>
              <td className="h-9 whitespace-nowrap border-r border-b border-[#e9edf3] bg-gray-100 px-2 py-1 text-center align-middle !text-[12px] !font-semibold !text-gray-800 last:border-r-0">
                {formatAmount(totals.unitPrice)}
              </td>
              <td className="h-9 whitespace-nowrap border-r border-b border-[#e9edf3] bg-gray-100 px-2 py-1 text-center align-middle !text-[12px] !font-semibold !text-gray-800 last:border-r-0">
                {formatAmount(totals.quantity)}
              </td>
              <td className="h-9 whitespace-nowrap border-r border-b border-[#e9edf3] bg-gray-100 px-2 py-1 text-center align-middle !text-[12px] !font-semibold !text-gray-800 last:border-r-0">
                {formatAmount(totals.discountAmount)}
              </td>
              <td className="h-9 whitespace-nowrap border-r border-b border-[#e9edf3] bg-gray-100 px-2 py-1 text-center align-middle !text-[12px] !font-semibold !text-gray-800 last:border-r-0">
                {formatAmount(totals.taxAmount)}
              </td>
              <td className="h-9 whitespace-nowrap border-r border-b border-[#e9edf3] bg-gray-100 px-2 py-1 text-center align-middle !text-[12px] !font-semibold !text-gray-800 last:border-r-0">
                {formatAmount(totals.netAmount)}
              </td>
              <td className="h-9 whitespace-nowrap border-r border-b border-[#e9edf3] bg-gray-100 px-2 py-1 text-center align-middle !text-[12px] !font-semibold !text-gray-800 last:border-r-0">
                {formatAmount(totals.patientShare)}
              </td>
              <td className="h-9 whitespace-nowrap border-r border-b border-[#e9edf3] bg-gray-100 px-2 py-1 text-center align-middle !text-[12px] !font-semibold !text-gray-800 last:border-r-0">
                {formatAmount(totals.payerShare)}
              </td>
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
};

export default PreAuthItemsTable;
